import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import {
  Boxes,
  Building2,
  CheckCircle2,
  ClipboardList,
  PackageOpen,
  PoundSterling,
  Receipt,
  ShoppingBag,
  Truck,
  TrendingUp,
} from 'lucide-react';
import { useAuth } from '../auth/useAuth';
import { hasRole, primaryCustomerAdminOrgId, primaryOrgId } from '../auth/permissions';
import { getOverview, getDashboard } from '../api/reporting';
import type { DashboardSummary, MspDashboard, OrgAdminDashboard, MemberDashboard } from '../api/reporting';
import { Card, CardBody } from '../components/ui/Card';
import { PageHeader } from '../components/ui/PageHeader';
import { Spinner } from '../components/ui/Spinner';

type Tone = 'default' | 'amber' | 'red';

const toneClasses: Record<Tone, string> = {
  default: 'bg-brand-50 text-brand-600',
  amber: 'bg-amber-50 text-amber-600',
  red: 'bg-red-50 text-red-600',
};

function StatCard({
  icon: Icon,
  label,
  value,
  to,
  tone = 'default',
}: {
  icon: typeof Boxes;
  label: string;
  value: string;
  to?: string;
  tone?: Tone;
}) {
  const content = (
    <CardBody className="flex items-center gap-4">
      <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-lg ${toneClasses[tone]}`}>
        <Icon className="h-5 w-5" />
      </div>
      <div>
        <p className="text-xs font-medium uppercase tracking-wide text-slate-500">{label}</p>
        <p className="mt-0.5 text-xl font-semibold text-slate-900">{value}</p>
      </div>
    </CardBody>
  );

  if (to) {
    return (
      <Card className="transition-shadow hover:shadow-md">
        <Link to={to}>{content}</Link>
      </Card>
    );
  }
  return <Card>{content}</Card>;
}

function StatGrid({ children }: { children: React.ReactNode }) {
  return <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">{children}</div>;
}

// Explicit type-predicate guards rather than an inline `d?.scope === 'X' ?
// d : null` ternary - the latter doesn't reliably narrow the base
// expression across every TS setup, and a guard function removes any doubt.
function isMspDashboard(d: DashboardSummary | undefined): d is MspDashboard {
  return !!d && d.scope === 'MSP_ADMIN';
}
function isOrgAdminDashboard(d: DashboardSummary | undefined): d is OrgAdminDashboard {
  return !!d && d.scope === 'CUSTOMER_ADMIN';
}
function isMemberDashboard(d: DashboardSummary | undefined): d is MemberDashboard {
  return !!d && d.scope === 'MEMBER';
}

function MspAdminDashboard() {
  const overviewQuery = useQuery({ queryKey: ['reporting', 'overview'], queryFn: getOverview });
  const dashboardQuery = useQuery({ queryKey: ['reporting', 'dashboard'], queryFn: getDashboard });
  const dashboardData = dashboardQuery.data;
  const d = isMspDashboard(dashboardData) ? dashboardData : null;

  return (
    <div>
      <PageHeader title="Dashboard" description="Overview across every customer." />

      {dashboardQuery.isLoading ? (
        <Spinner />
      ) : d ? (
        <div className="mb-6">
          <StatGrid>
            <StatCard
              icon={CheckCircle2}
              label="Pending approvals"
              value={String(d.pendingApprovals)}
              to="/approvals"
              tone={d.pendingApprovals > 0 ? 'amber' : 'default'}
            />
            <StatCard icon={ClipboardList} label="Orders in flight" value={String(d.ordersInFlight)} to="/orders" />
            <StatCard icon={ShoppingBag} label="Awaiting procurement" value={String(d.pendingProcurementLines)} to="/procurement" />
            <StatCard icon={Truck} label="Awaiting delivery" value={String(d.deliverableLines)} to="/delivery" />
            <StatCard
              icon={PackageOpen}
              label="Loan requests pending"
              value={String(d.pendingLoanApprovals)}
              to="/loans"
              tone={d.pendingLoanApprovals > 0 ? 'amber' : 'default'}
            />
            <StatCard
              icon={PackageOpen}
              label="Loans overdue"
              value={String(d.overdueLoans)}
              to="/loans"
              tone={d.overdueLoans > 0 ? 'red' : 'default'}
            />
            <StatCard icon={Receipt} label="Invoices due within 14 days" value={String(d.invoicesDueSoon)} />
          </StatGrid>
        </div>
      ) : null}

      {overviewQuery.isLoading ? (
        <Spinner />
      ) : overviewQuery.data ? (
        <StatGrid>
          <StatCard icon={Building2} label="Customers" value={String(overviewQuery.data.totalCustomers)} to="/customers" />
          <StatCard
            icon={Boxes}
            label="Assets in service"
            value={String(overviewQuery.data.assetsByStatus.find((a) => a.status === 'IN_SERVICE')?.count ?? 0)}
            to="/assets"
          />
          <StatCard icon={PoundSterling} label="Billed to date" value={`£${overviewQuery.data.historicalTotal.toFixed(2)}`} />
          <StatCard
            icon={TrendingUp}
            label="Forecast recurring revenue"
            value={`£${overviewQuery.data.forecastRemainingManaged.toFixed(2)}`}
          />
        </StatGrid>
      ) : null}
    </div>
  );
}

function CustomerAdminDashboard({ orgId }: { orgId: string }) {
  const dashboardQuery = useQuery({ queryKey: ['reporting', 'dashboard'], queryFn: getDashboard });
  const dashboardData = dashboardQuery.data;
  const d = isOrgAdminDashboard(dashboardData) ? dashboardData : null;

  return (
    <div>
      <PageHeader title="Dashboard" description="Your organisation at a glance." />

      {dashboardQuery.isLoading ? (
        <Spinner />
      ) : d ? (
        <div className="mb-6">
          <StatGrid>
            <StatCard
              icon={CheckCircle2}
              label="Pending approvals"
              value={String(d.pendingApprovals)}
              to="/orders"
              tone={d.pendingApprovals > 0 ? 'amber' : 'default'}
            />
            <StatCard icon={ClipboardList} label="Orders in flight" value={String(d.ordersInFlight)} to="/orders" />
            <StatCard icon={PackageOpen} label="Active loans" value={String(d.activeLoans)} to="/loans" />
            <StatCard
              icon={PackageOpen}
              label="Loans overdue"
              value={String(d.overdueLoans)}
              to="/loans"
              tone={d.overdueLoans > 0 ? 'red' : 'default'}
            />
            <StatCard
              icon={Receipt}
              label="Next invoice"
              value={d.nextInvoiceDate ? new Date(d.nextInvoiceDate).toLocaleDateString() : '-'}
              to={`/customers/${orgId}`}
            />
          </StatGrid>
        </div>
      ) : null}

      <div className="flex flex-col gap-3">
        <Card>
          <CardBody>
            <p className="text-sm text-slate-600">
              Manage your sites, users and catalog from{' '}
              <Link to={`/customers/${orgId}`} className="font-medium text-brand-600 hover:underline">
                My Organisation
              </Link>
              .
            </p>
          </CardBody>
        </Card>
        <Card>
          <CardBody>
            <p className="text-sm text-slate-600">
              Place a new order from the{' '}
              <Link to="/shop" className="font-medium text-brand-600 hover:underline">
                Shop
              </Link>
              , or review past orders under{' '}
              <Link to="/orders" className="font-medium text-brand-600 hover:underline">
                Orders
              </Link>
              .
            </p>
          </CardBody>
        </Card>
      </div>
    </div>
  );
}

function GenericDashboard({ user }: { user: ReturnType<typeof useAuth>['user'] }) {
  const canOrder = !!primaryOrgId(user);
  const isSigningAuthority = hasRole(user, 'SIGNING_AUTHORITY');
  const dashboardQuery = useQuery({ queryKey: ['reporting', 'dashboard'], queryFn: getDashboard });
  const dashboardData = dashboardQuery.data;
  const d = isMemberDashboard(dashboardData) ? dashboardData : null;

  return (
    <div>
      <PageHeader title="Dashboard" />

      {dashboardQuery.isLoading ? (
        <Spinner />
      ) : d ? (
        <div className="mb-6">
          <StatGrid>
            {isSigningAuthority && (
              <StatCard
                icon={CheckCircle2}
                label="Pending your approval"
                value={String(d.pendingApprovals)}
                to="/approvals"
                tone={d.pendingApprovals > 0 ? 'amber' : 'default'}
              />
            )}
            {isSigningAuthority && (
              <StatCard
                icon={PackageOpen}
                label="Loan requests pending"
                value={String(d.pendingLoanApprovals)}
                to="/loans"
                tone={d.pendingLoanApprovals > 0 ? 'amber' : 'default'}
              />
            )}
            {canOrder && (
              <StatCard
                icon={ClipboardList}
                label="Your orders pending approval"
                value={String(d.myOrdersPendingApproval)}
                to="/orders"
                tone={d.myOrdersPendingApproval > 0 ? 'amber' : 'default'}
              />
            )}
            {canOrder && (
              <StatCard icon={ClipboardList} label="Your orders in flight" value={String(d.myOrdersInFlight)} to="/orders" />
            )}
            <StatCard icon={PackageOpen} label="Your active loans" value={String(d.myActiveLoans)} to="/loans" />
            <StatCard
              icon={PackageOpen}
              label="Your loans overdue"
              value={String(d.myOverdueLoans)}
              to="/loans"
              tone={d.myOverdueLoans > 0 ? 'red' : 'default'}
            />
          </StatGrid>
        </div>
      ) : null}

      <div className="flex flex-col gap-3">
        {canOrder && (
          <Card>
            <CardBody>
              <p className="text-sm text-slate-600">
                Need something?{' '}
                <Link to="/shop" className="font-medium text-brand-600 hover:underline">
                  Browse the shop
                </Link>{' '}
                and submit a new order, or borrow equipment from{' '}
                <Link to="/loans" className="font-medium text-brand-600 hover:underline">
                  Loans
                </Link>
                .
              </p>
            </CardBody>
          </Card>
        )}
        {!canOrder && !isSigningAuthority && (
          <Card>
            <CardBody>
              <p className="text-sm text-slate-600">Nothing to show here yet.</p>
            </CardBody>
          </Card>
        )}
      </div>
    </div>
  );
}

export function DashboardPage() {
  const { user } = useAuth();
  const myOrgId = primaryCustomerAdminOrgId(user);

  if (user?.isMspAdmin) return <MspAdminDashboard />;
  if (myOrgId) return <CustomerAdminDashboard orgId={myOrgId} />;
  return <GenericDashboard user={user} />;
}
